import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Modal,
  Box,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Popover,
  Typography,
  Checkbox,
  Autocomplete,
  Menu,
  Divider,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Edit,
  Delete,
  Print,
  Visibility,
  Add,
  Close,
  AttachFile,
} from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Chip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useMediaQuery, useTheme } from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  Description as FileIcon,
} from "@mui/icons-material";
import { Collapse } from "@mui/material";
import { generateContraPdf } from "./ContraVoucherPrint";
import { ToWords } from "to-words";
import InfiniteScroll from "react-infinite-scroll-component";
import config from "../../../config";
import { FileDownload } from "@mui/icons-material";
import dayjs from "dayjs";
import { CircularProgress } from "@mui/material";

import {
  ContraVoucherList,
  UpdateContraVoucher,
  DeleteContraVoucher,
  ApproveContraVoucher,
  ExportContraVoucher,
} from "../../../Services/Services";

const TYPE_MAP = {
  "CASH TO BANK": "Cash to Bank",
  "BANK TO CASH": "Bank to Cash",
  "BANK TO BANK": "Bank to Bank",
};
const toKey = (v = "") => v.replace(/\s+/g, "_").toUpperCase();
const toLabel = (v = "") => TYPE_MAP[toKey(v)] || v;
const typeOptions = Object.entries(TYPE_MAP).map(([value, label]) => ({
  value,
  label,
}));
const toWords = new ToWords({ localeCode: "en-IN" });

const toDateInput = (d) => (d ? String(d).slice(0, 10) : "");

function ContraVoucher() {
  const [contraVoucher, setContraVouchers] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const navigate = useNavigate();
  const [approveAnchorEl, setApproveAnchorEl] = useState(null);
  const [ContraToApprove, setContraToApprove] = useState(null);
  const [remarkInput, setRemarkInput] = useState("");
  const [actionType, setActionType] = useState(null);
  const [showRemarkForIndentId, setShowRemarkForIndentId] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedContra, setExpandedContra] = useState(null);

  // 🆕 Date Range States
  const [dateMenuAnchor, setDateMenuAnchor] = useState(null);
  const [selectingDate, setSelectingDate] = useState(null);
  const dateInputRef = useRef(null);
  const [showCustomDateInputs, setShowCustomDateInputs] = useState(false);
  const [tempCustomDateRange, setTempCustomDateRange] = useState({
    from: "",
    to: "",
  });
  const [customDateRange, setCustomDateRange] = useState({
    from: "",
    to: "",
  });

  const [paymentDateMenuAnchor, setContraDateMenuAnchor] = useState(null);
  const [contraDateRange, setPaymentDateRange] = useState({
    from: "",
    to: "",
  });
  const [tempContraDateRange, setTempContraDateRange] = useState({
    from: "",
    to: "",
  });

  // Edit states
  const [editOpen, setEditOpen] = useState(false);
  const [editingVoucher, setEditingVoucher] = useState(null);
  const [editForm, setEditForm] = useState({
    voucher_no: "",
    voucher_type: "",
    cash_amount: "",
    narration: "",
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [isExcelLoading, setExportLoading] = useState(false);

  // ✅ Updated filters with date_range
  const [filters, setFilters] = useState({
    date_range: "today", // 🆕 Added
    voucher_no: "",
    account_no: "",
    from_date: "",
    to_date: "",
    contra_date_from: "",
    contra_date_to: "",
  });

  const handleExportContraVoucher = async () => {
    try {
      if (contraVoucher.length === 0) {
        toast.warning("No data available to export");
        return;
      }
      setExportLoading(true);
      const params = { ...filters };

      // Add custom dates if custom range selected
      if (
        filters.date_range === "custom" &&
        customDateRange.from &&
        customDateRange.to
      ) {
        params.from_date = customDateRange.from;
        params.to_date = customDateRange.to;
      }

      // Add contra date range if selected
      if (contraDateRange.from && contraDateRange.to) {
        params.contra_date_from = contraDateRange.from;
        params.contra_date_to = contraDateRange.to;
      }

      console.log("Export Params:", params);

      const blob = await ExportContraVoucher(params);

      if (!blob || blob.size === 0) {
        toast.warning("No data available to export");
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      // Generate filename with current date/time
      const filename = `contra_voucher_export_${dayjs().format(
        "YYYY-MM-DD_HH-mm-ss"
      )}.xlsx`;
      link.setAttribute("download", filename);

      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Export successful!");
    } catch (error) {
      console.error("Export Error:", error);
      toast.error(error.message || "Failed to export contra vouchers");
    } finally {
      setExportLoading(false);
    }
  };

  // Delete states
  const [deleteAnchorEl, setDeleteAnchorEl] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const userRole = localStorage.getItem("role");

  // 🆕 Helper function to format date display
  const formatDateDisplay = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  // 🆕 Get date range display text
  const getDateRangeDisplay = (rangeType) => {
    const now = new Date();
    const formatDate = (date) => {
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    switch (rangeType) {
      case "today":
        return formatDate(now);
      case "yesterday":
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return formatDate(yesterday);
      case "this_week":
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1);
        return `${formatDate(weekStart)} to ${formatDate(now)}`;
      case "previous_week":
        const prevWeekEnd = new Date(now);
        prevWeekEnd.setDate(prevWeekEnd.getDate() - prevWeekEnd.getDay());
        const prevWeekStart = new Date(prevWeekEnd);
        prevWeekStart.setDate(prevWeekStart.getDate() - 6);
        return `${formatDate(prevWeekStart)} to ${formatDate(prevWeekEnd)}`;
      case "this_month":
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return `${formatDate(monthStart)} to ${formatDate(now)}`;
      case "current_year":
        const yearStart = new Date(now.getFullYear(), 0, 1);
        return `${formatDate(yearStart)} to ${formatDate(now)}`;
      case "financial_year":
        let fyStart;
        if (now.getMonth() >= 3) {
          fyStart = new Date(now.getFullYear(), 3, 1);
        } else {
          fyStart = new Date(now.getFullYear() - 1, 3, 1);
        }
        return `${formatDate(fyStart)} to ${formatDate(now)}`;
      default:
        return "";
    }
  };

  // ✅ Updated useEffect
  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      loadVouchers(1, false);
    }, 500);

    return () => clearTimeout(handler);
  }, [filters, customDateRange, contraDateRange]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  // ✅ Updated loadVouchers
  const loadVouchers = async (
    pageNum = 1,
    reset = false,
    filterValues = filters,
    append = false
  ) => {
    try {
      // 🆕 Build params with custom date handling
      const params = { ...filterValues };

      // Add custom dates if custom range selected
      if (
        filterValues.date_range === "custom" &&
        customDateRange.from &&
        customDateRange.to
      ) {
        params.from_date = customDateRange.from;
        params.to_date = customDateRange.to;
      }
      if (filterValues.contra_date_from && filterValues.contra_date_to) {
        params.contra_date_from = filterValues.contra_date_from;
        params.contra_date_to = filterValues.contra_date_to;
      }
      console.log("API Params being sent:", params);

      const data = await ContraVoucherList(params, pageNum);
      if (reset || pageNum === 1) {
        setContraVouchers(Array.isArray(data) ? data : data.results || []);
      } else {
        setContraVouchers((prev) => [
          ...prev,
          ...(Array.isArray(data) ? data : data.results),
        ]);
      }
      setHasMore(Array.isArray(data) ? data.next !== null : data.next !== null);
      setPage(pageNum);
    } catch (err) {
      toast.error("Failed to load vouchers");
      setHasMore(false);
    }
  };

  // ✅ Updated fetchMoreData
  const fetchMoreData = () => {
    if (!hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    loadVouchers(nextPage, false, filters);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleOpenEdit = (voucher) => {
    navigate("/dashboard/contra-voucher-create", {
      state: {
        editMode: true,
        voucherData: {
          ...voucher,
          cv_id: voucher.cv_id || null,
          voucher_no: voucher.voucher_no || "",
          date: toDateInput(voucher.date),
          voucher_type: voucher.voucher_type || "",
          cash_amount: voucher.cash_amount ?? "",
          from_account: voucher.from_account ?? voucher.from_account_id ?? "",
          to_account: voucher.to_account ?? voucher.to_account_id ?? "",
          narration: voucher.narration || "",
        },
      },
    });
  };

  // ✅ Updated handleReset
  const handleReset = () => {
    setFilters({
      date_range: "today", // 🆕 Reset to today
      voucher_no: "",
      account_no: "",
      contra_date_from: "",
      contra_date_to: "",
    });
    setCustomDateRange({ from: "", to: "" }); // 🆕 Reset custom dates
    setSelectingDate(null); // 🆕
    setPage(1);
    setTempCustomDateRange({ from: "", to: "" }); // ✅ Add
    setShowCustomDateInputs(false); // ✅ Add
    setPaymentDateRange({ from: "", to: "" });
    setTempContraDateRange({ from: "", to: "" });
  };

  const handleCancelCustomDateRange = () => {
    setTempCustomDateRange({ from: "", to: "" });
    setShowCustomDateInputs(false);
  };
  const handleApplyCustomDateRange = () => {
    if (tempCustomDateRange.from && tempCustomDateRange.to) {
      setCustomDateRange({
        from: tempCustomDateRange.from,
        to: tempCustomDateRange.to,
      });
      setFilters((prev) => ({ ...prev, date_range: "custom" }));
      setDateMenuAnchor(null);
      setShowCustomDateInputs(false);
    } else {
      toast.warning("Please select both From and To dates");
    }
  };
  const handleCloseEdit = () => {
    setEditOpen(false);
    setEditingVoucher(null);
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpdate = async () => {
    if (!editingVoucher) return;
    try {
      setActionLoading(true);
      const payload = {
        voucher_no: (editForm.voucher_no || "").trim(),
        date: editForm.date,
        voucher_type: editForm.voucher_type,
        cash_amount: Number(editForm.cash_amount || 0),
        narration: editForm.narration?.trim() || null,
      };
      const id = editingVoucher.cv_id;

      const res = await UpdateContraVoucher(id, payload);
      if (res && !res.error) {
        const updated = res?.cv_id ? res : { ...editingVoucher, ...payload };
        setContraVouchers((prev) =>
          prev.map((v) => (v.cv_id === id ? { ...v, ...updated } : v))
        );
        toast.success("Voucher updated");
        handleCloseEdit();
      } else {
        toast.error(res?.message || "Failed to update voucher");
      }
    } catch (e) {
      toast.error("Failed to update voucher");
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditClick = async (voucher) => {
    navigate("/dashboard/contra-voucher-create", {
      state: {
        editMode: true,
        voucherData: voucher,
      },
    });
  };

  const handleApprove = async (voucher) => {
    const approvedById = Number(localStorage.getItem("user_id"));
    if (!approvedById) {
      toast.error("Approver ID not found.");
      return;
    }

    try {
      const updated = await ApproveContraVoucher(
        voucher.cv_id,
        approvedById,
        voucher.status,
        remarkInput
      );

      setContraVouchers((prev) =>
        prev.map((item) => (item.cv_id === updated.cv_id ? updated : item))
      );

      toast.success("Contra Voucher approved successfully!");
    } catch (error) {
      console.error("Approval Error:", error);
      toast.error("Failed to approve Contra Voucher.");
    }
  };

  const truncateText = (text, limit = 20) => {
    if (!text) return "";
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  const openDeletePopover = (e, voucher) => {
    setDeleteAnchorEl(e.currentTarget);
    setToDelete(voucher);
  };

  const closeDeletePopover = () => {
    setDeleteAnchorEl(null);
    setToDelete(null);
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      setActionLoading(true);
      const id = toDelete.cv_id;
      const res = await DeleteContraVoucher(id);
      if (res && !res.error) {
        setContraVouchers((prev) => prev.filter((v) => v.cv_id !== id));
        toast.success("Voucher deleted");
      } else {
        toast.error(res?.message || "Failed to delete voucher");
      }
    } catch (e) {
      toast.error("Failed to delete voucher");
    } finally {
      setActionLoading(false);
      closeDeletePopover();
    }
  };

  // Handle Apply
  const handleApplyPaymentDateRange = () => {
    setPaymentDateRange(tempContraDateRange);
    setFilters((prev) => ({
      ...prev,
      contra_date_from: tempContraDateRange.from,
      contra_date_to: tempContraDateRange.to,
      date_range: "all",
    }));
    setContraDateMenuAnchor(null);
  };

  // Handle Cancel
  const handleCancelContraDateRange = () => {
    setTempContraDateRange(contraDateRange);
    setContraDateMenuAnchor(null);
  };

  // Handle Clear
  const handleClearContraDateRange = () => {
    setPaymentDateRange({ from: "", to: "" });
    setTempContraDateRange({ from: "", to: "" });
    setFilters((prev) => ({
      ...prev,
      contra_date_from: "",
      contra_date_to: "",
    }));
    setContraDateMenuAnchor(null);
  };

  useEffect(() => {
    document.body.style.overflow = isMobile ? "auto" : "hidden";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);
  // ✅ ADD THIS ENTIRE COMPONENT BEFORE return (
  const MobileContraCard = ({ voucher, index }) => {
    const isExpanded = expandedContra === voucher.cv_id;

    return (
      <Box key={voucher.cv_id}>
        {/* Main Row */}
        <Box
          onClick={() =>
            setExpandedContra(
              expandedContra === voucher.cv_id ? null : voucher.cv_id
            )
          }
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1.5,
            cursor: "pointer",
            bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
            "&:active": {
              bgcolor: "#e3f2fd",
            },
            borderLeft: `4px solid ${
              voucher.status === "Approved"
                ? "#4caf50"
                : voucher.status === "Rejected"
                ? "#f44336"
                : "#ff9800"
            }`,
          }}
        >
          <Box sx={{ flex: 1 }}>
            {/* Voucher Number & Type */}
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
            >
              <Typography
                sx={{ fontSize: "0.7rem", color: "#666", minWidth: 25 }}
              >
                #{index + 1}
              </Typography>
              <Typography
                sx={{ fontWeight: "bold", fontSize: "0.85rem", flex: 1 }}
              >
                {voucher.voucher_no}
              </Typography>
            </Box>

            {/* Type & Date */}
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "#666",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <span style={{ fontWeight: 500 }}>
                {toLabel(voucher.voucher_type)}
              </span>
              <span style={{ color: "#999" }}>•</span>
              <span style={{ fontSize: "0.7rem" }}>
                {new Date(voucher.date + "T00:00:00").toLocaleDateString(
                  "en-GB"
                )}
              </span>
            </Typography>
          </Box>

          {/* Expand Icon */}
          <IconButton size="small" sx={{ ml: 1 }}>
            {isExpanded ? (
              <ExpandLess fontSize="small" />
            ) : (
              <ExpandMore fontSize="small" />
            )}
          </IconButton>
        </Box>

        {/* Expanded Details */}
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
              borderLeft: `4px solid ${
                voucher.status === "Approved"
                  ? "#4caf50"
                  : voucher.status === "Rejected"
                  ? "#f44336"
                  : "#ff9800"
              }`,
            }}
          >
            {/* 2 Column Grid */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 0.8,
                mb: 1,
              }}
            >
              {/* Status */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                  }}
                >
                  STATUS
                </Typography>
                <Chip
                  label={voucher.status}
                  color={
                    voucher.status === "Approved"
                      ? "success"
                      : voucher.status === "Rejected"
                      ? "error"
                      : "warning"
                  }
                  size="small"
                  sx={{ height: 20, fontSize: "0.65rem", mt: 0.3 }}
                />
              </Box>

              {/* Amount */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                  }}
                >
                  AMOUNT
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: "#2e7d32",
                  }}
                >
                  ₹ {Number(voucher.cash_amount || 0).toLocaleString("en-IN")}
                </Typography>
              </Box>

              {/* From Account */}
              <Box sx={{ gridColumn: "1 / -1" }}>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                  }}
                >
                  FROM ACCOUNT
                </Typography>
                <Typography
                  sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}
                >
                  {voucher.from_account_name}
                </Typography>
              </Box>

              {/* To Account */}
              <Box sx={{ gridColumn: "1 / -1" }}>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                  }}
                >
                  TO ACCOUNT
                </Typography>
                <Typography
                  sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}
                >
                  {voucher.to_account_name}
                </Typography>
              </Box>

              {/* Created By */}
              {voucher.created_by_name && (
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.6rem",
                      color: "#999",
                      letterSpacing: "0.5px",
                    }}
                  >
                    CREATED BY
                  </Typography>
                  <Typography
                    sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}
                  >
                    {voucher.created_by_name}
                  </Typography>
                </Box>
              )}

              {/* Approved By */}
              {voucher.approved_name && (
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.6rem",
                      color: "#999",
                      letterSpacing: "0.5px",
                    }}
                  >
                    APPROVED BY
                  </Typography>
                  <Typography
                    sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}
                  >
                    {voucher.approved_name}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Narration - Full Width */}
            {voucher.narration && (
              <Box sx={{ mb: 0.8 }}>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                    mb: 0.3,
                  }}
                >
                  NARRATION
                </Typography>
                <Typography
                  sx={{ fontSize: "0.7rem", color: "#555", lineHeight: 1.3 }}
                >
                  {voucher.narration}
                </Typography>
              </Box>
            )}

            {/* Remarks */}
            {voucher.remarks && (
              <Box sx={{ mb: 0.8 }}>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: "#999",
                    letterSpacing: "0.5px",
                    mb: 0.3,
                  }}
                >
                  REMARKS
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#555",
                    fontStyle: "italic",
                    lineHeight: 1.3,
                  }}
                >
                  {voucher.remarks}
                </Typography>
              </Box>
            )}

            <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              {/* Approve - Admin Only */}
              {userRole != "Staff" && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    bgcolor:
                      voucher.status === "Approved"
                        ? "#e8f5e9"
                        : voucher.status === "Rejected"
                        ? "#ffebee"
                        : "#fff3e0",
                    px: 1,
                    py: 0.5,
                    borderRadius: "6px",
                    border: `1px solid ${
                      voucher.status === "Approved"
                        ? "#4caf50"
                        : voucher.status === "Rejected"
                        ? "#f44336"
                        : "#ff9800"
                    }`,
                  }}
                >
                  <Checkbox
                    checked={
                      voucher.status === "Approved" ||
                      voucher.status === "Rejected"
                    }
                    onChange={(e) => {
                      e.stopPropagation();
                      setApproveAnchorEl(e.currentTarget);
                      setContraToApprove(voucher);
                    }}
                    sx={{
                      padding: 0,
                      transform: "scale(0.6)",
                      color:
                        voucher.status === "Approved"
                          ? "#4caf50"
                          : voucher.status === "Rejected"
                          ? "#f44336"
                          : "#ff9800",
                      "&.Mui-checked": {
                        color:
                          voucher.status === "Approved" ? "#4caf50" : "#f44336",
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      color:
                        voucher.status === "Approved"
                          ? "#2e7d32"
                          : voucher.status === "Rejected"
                          ? "#c62828"
                          : "#f57c00",
                    }}
                  >
                    {voucher.status === "Approved"
                      ? "Approved"
                      : voucher.status === "Rejected"
                      ? "Rejected"
                      : "Approve"}
                  </Typography>
                </Box>
              )}

              {/* Action Icons */}
              <Box sx={{ display: "flex", gap: 0.6 }}>
                {/* Edit */}
                <IconButton
                  size="small"
                  disabled={voucher.status === "Approved"}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenEdit(voucher);
                  }}
                  sx={{
                    bgcolor:
                      voucher.status === "Approved" ? "#f5f5f5" : "#e8f5e9",
                    borderRadius: "6px",
                    width: 32,
                    height: 32,
                    "&:hover": {
                      bgcolor:
                        voucher.status === "Approved" ? "#f5f5f5" : "#c8e6c9",
                      transform:
                        voucher.status === "Approved" ? "none" : "scale(1.05)",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  <Edit
                    sx={{
                      fontSize: "1rem",
                      color:
                        voucher.status === "Approved" ? "#bdbdbd" : "#4caf50",
                    }}
                  />
                </IconButton>

                {/* Delete */}
                <IconButton
                  size="small"
                  disabled={voucher.status === "Approved"}
                  onClick={(e) => {
                    e.stopPropagation();
                    openDeletePopover(e, voucher);
                  }}
                  sx={{
                    bgcolor:
                      voucher.status === "Approved" ? "#f5f5f5" : "#ffebee",
                    borderRadius: "6px",
                    width: 32,
                    height: 32,
                    "&:hover": {
                      bgcolor:
                        voucher.status === "Approved" ? "#f5f5f5" : "#ffcdd2",
                      transform:
                        voucher.status === "Approved" ? "none" : "scale(1.05)",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  <Delete
                    sx={{
                      fontSize: "1rem",
                      color:
                        voucher.status === "Approved" ? "#bdbdbd" : "#f44336",
                    }}
                  />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Collapse>

        <Divider />
      </Box>
    );
  };

  return (
    <>
      <ToastContainer />

      {/* 🆕 Updated Filter Section */}
      <Box sx={{ mt: -5, pt: -3, mb: 2, width: "100%" }}>
        <Grid container spacing={2} alignItems="center">
          {/* Date Range Dropdown */}
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="Date Range"
              value={
                filters.date_range === "custom" &&
                customDateRange.from &&
                customDateRange.to
                  ? `${formatDateDisplay(
                      customDateRange.from
                    )} to ${formatDateDisplay(customDateRange.to)}`
                  : filters.date_range === "today"
                  ? "Today"
                  : filters.date_range === "yesterday"
                  ? "Yesterday"
                  : filters.date_range === "this_week"
                  ? "This Week"
                  : filters.date_range === "previous_week"
                  ? "Previous Week"
                  : filters.date_range === "this_month"
                  ? "This Month"
                  : filters.date_range === "current_year"
                  ? "Current Year"
                  : filters.date_range === "financial_year"
                  ? "Financial Year"
                  : "Select Date Range"
              }
              onClick={(e) => setDateMenuAnchor(e.currentTarget)}
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton size="small" edge="end">
                    <ArrowDropDownIcon />
                  </IconButton>
                ),
              }}
              sx={{
                cursor: "pointer",
                fontWeight: "bold",
                "& .MuiInputBase-input": {
                  fontSize: "0.8rem",
                  padding: "4px 8px",
                },
                "& .MuiInputBase-root": {
                  height: 30,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.6rem",
                },
              }}
            />
          </Grid>

          {/* Voucher No */}
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="Search by Voucher No"
              size="small"
              name="voucher_no"
              value={filters.voucher_no}
              onChange={handleFilterChange}
              sx={{
                "& .MuiInputBase-root": {
                  height: 30,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
            />
          </Grid>

          {/* Account No */}
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth
              label="From/To Account No"
              name="account_no"
              size="small"
              value={filters.account_no}
              onChange={handleFilterChange}
              sx={{
                "& .MuiInputBase-root": {
                  height: 30,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <TextField
              label="Date"
              value={
                contraDateRange.from && contraDateRange.to
                  ? `${formatDateDisplay(
                      contraDateRange.from
                    )} to ${formatDateDisplay(contraDateRange.to)}`
                  : contraDateRange.from
                  ? `From ${formatDateDisplay(contraDateRange.from)}`
                  : contraDateRange.to
                  ? `To ${formatDateDisplay(contraDateRange.to)}`
                  : ""
              }
              placeholder="Select Contra Date Range"
              onClick={(e) => {
                setContraDateMenuAnchor(e.currentTarget);
                setTempContraDateRange(contraDateRange);
              }}
              size="small"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <IconButton size="small" edge="end">
                    <ArrowDropDownIcon />
                  </IconButton>
                ),
              }}
              InputLabelProps={{ shrink: true }}
              sx={{
                flex: "1 1 100px",
                cursor: "pointer",
                "& .MuiInputBase-root": {
                  height: 35,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.65rem",
                },
                "& .MuiInputBase-input": {
                  cursor: "pointer",
                },
              }}
            />
          </Grid>

          <Menu
            anchorEl={paymentDateMenuAnchor}
            open={Boolean(paymentDateMenuAnchor)}
            onClose={handleCancelContraDateRange}
            PaperProps={{
              sx: {
                width: 320,
                p: 0,
              },
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 2,
                bgcolor: "#fafafa",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, fontWeight: 600, color: "#1976d2" }}
              >
                Select Contra Date Range
              </Typography>

              {/* From and To Date Inputs */}
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <TextField
                  label="From Date"
                  type="date"
                  size="small"
                  fullWidth
                  value={tempContraDateRange.from}
                  onChange={(e) =>
                    setTempContraDateRange((prev) => ({
                      ...prev,
                      from: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    max:
                      tempContraDateRange.to ||
                      new Date().toISOString().split("T")[0],
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-root": {
                      height: 40,
                      fontSize: "0.75rem",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.75rem",
                    },
                  }}
                />

                <TextField
                  label="To Date"
                  type="date"
                  size="small"
                  fullWidth
                  value={tempContraDateRange.to}
                  onChange={(e) =>
                    setTempContraDateRange((prev) => ({
                      ...prev,
                      to: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: tempContraDateRange.from || undefined,
                    max: new Date().toISOString().split("T")[0],
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-root": {
                      height: 40,
                      fontSize: "0.75rem",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.75rem",
                    },
                  }}
                />
              </Box>

              {/* Selected Range Preview */}
              {tempContraDateRange.from && tempContraDateRange.to && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mb: 2,
                    p: 1,
                    bgcolor: "#e8f5e9",
                    borderRadius: 1,
                    textAlign: "center",
                    color: "#2e7d32",
                    fontWeight: 500,
                  }}
                >
                  📅 {formatDateDisplay(tempContraDateRange.from)} to{" "}
                  {formatDateDisplay(tempContraDateRange.to)}
                </Typography>
              )}

              {/* Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  justifyContent: "space-between",
                }}
              >
                <Button
                  variant="text"
                  size="small"
                  color="error"
                  onClick={handleClearContraDateRange}
                  disabled={!contraDateRange.from && !contraDateRange.to}
                  sx={{
                    fontSize: "0.7rem",
                    textTransform: "none",
                  }}
                >
                  Clear
                </Button>

                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCancelContraDateRange}
                    sx={{
                      fontSize: "0.7rem",
                      textTransform: "none",
                      borderRadius: 1,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleApplyPaymentDateRange}
                    disabled={
                      !tempContraDateRange.from || !tempContraDateRange.to
                    }
                    sx={{
                      fontSize: "0.7rem",
                      textTransform: "none",
                      borderRadius: 1,
                    }}
                  >
                    Apply
                  </Button>
                </Box>
              </Box>
            </Box>
          </Menu>

          {/* Reset Button - Mobile: 50% width */}
          <Grid item xs={6} sm={1}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              size="small"
              onClick={handleReset}
              sx={{
                height: 30,
                fontSize: "0.7rem",
                borderRadius: 0,
                whiteSpace: "nowrap",
              }}
            >
              Reset
            </Button>
          </Grid>

          {/* Add Contra Button - Mobile: 50% width */}
          <Grid item xs={6} sm={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate("/dashboard/contra-voucher-create")}
              sx={{
                height: 30,
                fontSize: "0.7rem",
                borderRadius: 0,
                whiteSpace: "nowrap",
                display: "flex",
                alignItems: "center",
              }}
            >
              <AddIcon fontSize="small" sx={{ mr: 0.5 }} />
              Contra
            </Button>
          </Grid>

          <Grid item xs={6} sm={1}>
            <Button
              variant="contained"
              color="success"
              onClick={handleExportContraVoucher}
              disabled={isExcelLoading || contraVoucher.length === 0}
              fullWidth
              sx={{
                height: 30,
                fontSize: "0.7rem",
                borderRadius: 0,
                whiteSpace: "nowrap",
              }}
            >
              {isExcelLoading ? (
                <CircularProgress size={16} color="inherit" sx={{ mr: 0.5 }} />
              ) : (
                <FileIcon fontSize="small" />
              )}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Date Range Menu - UPDATED */}
      <Menu
        anchorEl={dateMenuAnchor}
        open={Boolean(dateMenuAnchor)}
        onClose={() => {
          setDateMenuAnchor(null);
          setShowCustomDateInputs(false);
          setTempCustomDateRange({ from: "", to: "" });
        }}
        PaperProps={{
          sx: {
            width: 350,
            maxHeight: 500,
            "& .MuiTypography-root": {
              fontSize: "0.7rem",
            },
          },
        }}
      >
        {[
          { key: "today", label: "Today" },
          { key: "yesterday", label: "Yesterday" },
          { key: "this_week", label: "This Week" },
          { key: "previous_week", label: "Previous Week" },
          { key: "this_month", label: "This Month" },
          { key: "current_year", label: "Current Year" },
          { key: "financial_year", label: "Financial Year" },
        ].map((range) => (
          <MenuItem
            key={range.key}
            onClick={() => {
              setFilters((prev) => ({ ...prev, date_range: range.key }));
              setCustomDateRange({ from: "", to: "" });
              setDateMenuAnchor(null);
              setShowCustomDateInputs(false);
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Typography variant="body2">{range.label}</Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.65rem" }}
              >
                {getDateRangeDisplay(range.key)}
              </Typography>
            </Box>
          </MenuItem>
        ))}

        <Divider />

        {/* ✅ Custom Date Range - Expandable */}
        <MenuItem
          onClick={() => setShowCustomDateInputs(!showCustomDateInputs)}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: showCustomDateInputs ? "#e3f2fd" : "inherit",
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: showCustomDateInputs ? 600 : 400 }}
          >
            Custom Date Range
          </Typography>
          {showCustomDateInputs ? <ExpandLess /> : <ExpandMore />}
        </MenuItem>

        {/* ✅ Both Date Pickers Together */}
        <Collapse in={showCustomDateInputs} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 2,
              py: 2,
              bgcolor: "#f5f5f5",
              borderTop: "1px solid #e0e0e0",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 1, display: "block" }}
            >
              Select Date Range
            </Typography>

            {/* From and To Date Inputs Side by Side */}
            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                label="From Date"
                type="date"
                size="small"
                value={tempCustomDateRange.from}
                onChange={(e) =>
                  setTempCustomDateRange((prev) => ({
                    ...prev,
                    from: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max:
                    tempCustomDateRange.to ||
                    new Date().toISOString().split("T")[0],
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    height: 40,
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />

              <TextField
                label="To Date"
                type="date"
                size="small"
                value={tempCustomDateRange.to}
                onChange={(e) =>
                  setTempCustomDateRange((prev) => ({
                    ...prev,
                    to: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: tempCustomDateRange.from || undefined,
                  max: new Date().toISOString().split("T")[0],
                }}
                sx={{
                  flex: 1,
                  "& .MuiInputBase-root": {
                    height: 40,
                    fontSize: "0.75rem",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "0.75rem",
                  },
                }}
              />
            </Box>

            {/* Selected Range Preview */}
            {tempCustomDateRange.from && tempCustomDateRange.to && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1.5,
                  p: 1,
                  bgcolor: "#e8f5e9",
                  borderRadius: 1,
                  textAlign: "center",
                  color: "#2e7d32",
                  fontWeight: 500,
                }}
              >
                📅 {formatDateDisplay(tempCustomDateRange.from)} to{" "}
                {formatDateDisplay(tempCustomDateRange.to)}
              </Typography>
            )}

            {/* Apply and Cancel Buttons */}
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCancelCustomDateRange}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  borderRadius: 1,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleApplyCustomDateRange}
                disabled={!tempCustomDateRange.from || !tempCustomDateRange.to}
                sx={{
                  fontSize: "0.7rem",
                  textTransform: "none",
                  borderRadius: 1,
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Menu>
      <TableContainer
        // id="contra-scroll"
        component={Paper}
      >
        {/* ✅ MODIFIED - Conditional Rendering for Mobile/Desktop */}
        <InfiniteScroll
          dataLength={contraVoucher.length}
          next={fetchMoreData}
          hasMore={hasMore}
          loader={
            <Typography textAlign="center" p={2}>
              Loading...
            </Typography>
          }
          endMessage={
            <Typography textAlign="center" p={2}>
              No more vouchers!
            </Typography>
          }
        >
          {isMobile ? (
            // ✅ Mobile View
            <Box sx={{ bgcolor: "background.paper", pb: 2 }}>
              {contraVoucher.map((voucher, index) => (
                <MobileContraCard
                  key={`${
                    voucher.cv_id || voucher.voucher_no || index
                  }-${index}`}
                  voucher={voucher}
                  index={index}
                />
              ))}
            </Box>
          ) : (
            // Desktop Table View
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: "88vh",
                overflow: "auto",
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 1600,
                  "& td, & th": {
                    borderBottom: "1px solid #ccc",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "#f5f5f5",
                      "& th": {
                        fontWeight: "bold",
                        color: "#333",
                        borderBottom: "1px solid #999",
                        whiteSpace: "nowrap",
                        fontSize: "0.7rem",
                      },
                    }}
                  >
                    {userRole != "Staff" && (
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          // backgroundColor: "#fafafa !important",
                          zIndex: 4,
                        }}
                      >
                        Approve
                      </TableCell>
                    )}
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: userRole != "Staff" ? 80 : 0,
                        backgroundColor: "#f5f5f5",
                        zIndex: 4,
                      }}
                    >
                      S.No.
                    </TableCell>
                    <TableCell>Voucher No</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Voucher Type</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>From Account</TableCell>
                    <TableCell>To Account</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Narration</TableCell>
                    <TableCell>Created By</TableCell>
                    <TableCell>Approved By</TableCell>
                    <TableCell>Remarks</TableCell>
                    <TableCell
                      sx={{
                        position: "sticky",
                        backgroundColor: "#f5f5f5 !important",
                        zIndex: 3,
                        right: 0,
                        padding: "0 !important",
                      }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {contraVoucher.map((voucher, index) => (
                    <TableRow
                      hover
                      key={`${
                        voucher.cv_id || voucher.voucher_no || index
                      }-${index}`}
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#fafafa" : "#ffffff",
                        "&:hover": {
                          backgroundColor: "#e3f2fd",
                          "& td": {
                            backgroundColor: "#e3f2fd !important",
                          },
                        },
                        "& td": {
                          paddingRight: "13px !important",
                          paddingLeft: "13px !important",
                          padding: "0px",
                          fontSize: { xs: "0.65rem", sm: "0.75rem" },
                          whiteSpace: "nowrap",
                          color: (theme) =>
                            theme.palette.mode === "dark" ? "white" : "inherit",
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark" ? "black" : "inherit",
                        },
                      }}
                    >
                      {userRole != "Staff" && (
                        <TableCell
                          sx={{
                            position: "sticky",
                            left: 0,
                            // backgroundColor: "#fafafa !important",
                            zIndex: 3,
                          }}
                        >
                          <Checkbox
                            checked={
                              voucher.status === "Approved" ||
                              voucher.status === "Rejected"
                            }
                            onChange={(e) => {
                              setApproveAnchorEl(e.currentTarget);
                              setContraToApprove(voucher);
                            }}
                            sx={{
                              padding: 0,
                              paddingLeft: 1,
                              paddingRight: 1,
                              transform: "scale(0.8)",
                              color:
                                voucher.status === "Approved"
                                  ? "green"
                                  : voucher.status === "Rejected"
                                  ? "red"
                                  : "grey",
                              "&.Mui-checked": {
                                color:
                                  voucher.status === "Approved"
                                    ? "green"
                                    : "red",
                              },
                            }}
                          />
                        </TableCell>
                      )}
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: userRole != "Staff" ? 80 : 0,
                          // backgroundColor: "#f5f5f5 !important",
                          zIndex: 3,
                        }}
                      >
                        {index + 1}
                      </TableCell>

                      <TableCell>{voucher.voucher_no}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={
                            voucher.status === "Approved"
                              ? "Approved"
                              : voucher.status === "Rejected"
                              ? "Rejected"
                              : "Pending"
                          }
                          color={
                            voucher.status === "Approved"
                              ? "success"
                              : voucher.status === "Rejected"
                              ? "error"
                              : "warning"
                          }
                          variant="outlined"
                          size="small"
                          sx={{
                            // fontSize: "0.2rem", // 👈 font chhota hoga
                            height: "10px",
                            transform: "scale(0.8)",
                            "& .MuiChip-label": {
                              padding: "0 6px",
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell>{toLabel(voucher.voucher_type)}</TableCell>
                      <TableCell>{voucher.cash_amount}</TableCell>
                      <TableCell>{voucher.from_account_name}</TableCell>
                      <TableCell>{voucher.to_account_name}</TableCell>
                      <TableCell>
                        {new Date(voucher.date).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })}
                      </TableCell>
                      <TableCell>{voucher.narration}</TableCell>
                      <TableCell>{voucher.created_by_name}</TableCell>
                      <TableCell>{voucher.approved_name}</TableCell>
                      <TableCell>
                        <Tooltip title={voucher.remarks || ""} arrow>
                          <span>{truncateText(voucher.remarks || "", 20)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          position: "sticky",
                          // backgroundColor: "#f5f5f5 !important",
                          zIndex: 0,
                          right: 0,
                          padding: "0 !important",
                        }}
                      >
                        <IconButton
                          sx={{ padding: 0 }}
                          color="primary"
                          size="small"
                          onClick={() => handleOpenEdit(voucher)}
                          disabled={voucher.status === "Approved"}
                        >
                          <Edit transform="scale(0.6)" />
                        </IconButton>

                        <IconButton
                          sx={{ padding: 0 }}
                          color="error"
                          size="small"
                          onClick={(e) => openDeletePopover(e, voucher)}
                          disabled={voucher.status === "Approved"}
                        >
                          <Delete transform="scale(0.6)" />
                        </IconButton>
                        <IconButton
                          sx={{ padding: 0 }}
                          color="info"
                          size="small"
                          onClick={() => generateContraPdf(voucher)}
                        >
                          <Print transform="scale(0.6)" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </InfiniteScroll>
      </TableContainer>

      {/* Delete confirm popover */}
      <Popover
        open={Boolean(deleteAnchorEl)}
        anchorEl={deleteAnchorEl}
        onClose={closeDeletePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body2">
            Are you sure you want to delete?
          </Typography>
          <Box
            sx={{ mt: 1, display: "flex", gap: 1, justifyContent: "flex-end" }}
          >
            <Button size="small" onClick={closeDeletePopover}>
              No
            </Button>
            <Button
              size="small"
              color="error"
              onClick={confirmDelete}
              disabled={actionLoading}
            >
              Yes
            </Button>
          </Box>
        </Box>
      </Popover>
      {/* approve pop */}

      <Popover
        open={Boolean(approveAnchorEl)}
        anchorEl={approveAnchorEl}
        onClose={() => {
          setApproveAnchorEl(null);
          setContraToApprove(null);
          setRemarkInput("");
          setShowRemarkForIndentId(null);
          setActionType(null);
        }}
        anchorReference="anchorPosition"
        anchorPosition={{
          top: window.innerHeight / 2,
          left: window.innerWidth / 2,
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "center",
        }}
        PaperProps={{
          sx: {
            maxWidth: { xs: "95vw", sm: "90vw", md: "85vw", lg: 1100 },
            maxHeight: "90vh",
            overflow: "auto",
            borderRadius: { xs: 1, sm: 2 },
          },
        }}
      >
        <Box
          sx={{
            py: { xs: 1, sm: 1.5, md: 2 },
            px: { xs: 1, sm: 1.5, md: 2 },
            minWidth: { xs: "auto", sm: 500, md: 700, lg: 1050 },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          {/* ✅ MOBILE: Card Layout, DESKTOP: Table */}
          {isMobile ? (
            // 📱 MOBILE CARD VIEW
            <Box>
              {/* Header Card */}
              <Box
                sx={{
                  bgcolor: "#e3f2fd",
                  p: 1.5,
                  borderRadius: 1,
                  mb: 1.5,
                  border: "2px solid #1976d2",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    textAlign: "center",
                    color: "#1976d2",
                    mb: 1,
                  }}
                >
                  CONTRA VOUCHER
                </Typography>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{ fontSize: "0.65rem", color: "#666", mb: 0.3 }}
                    >
                      Voucher No:
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
                      {ContraToApprove?.voucher_no || "-"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      sx={{ fontSize: "0.65rem", color: "#666", mb: 0.3 }}
                    >
                      Status:
                    </Typography>
                    <Chip
                      label={ContraToApprove?.status || "Pending"}
                      color={
                        ContraToApprove?.status === "Approved"
                          ? "success"
                          : ContraToApprove?.status === "Rejected"
                          ? "error"
                          : "warning"
                      }
                      size="small"
                      sx={{ height: 22, fontSize: "0.7rem" }}
                    />
                  </Box>

                  <Box>
                    <Typography
                      sx={{ fontSize: "0.65rem", color: "#666", mb: 0.3 }}
                    >
                      Date:
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
                      {ContraToApprove?.date
                        ? dayjs(ContraToApprove.date).format("DD/MM/YYYY")
                        : "-"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      sx={{ fontSize: "0.65rem", color: "#666", mb: 0.3 }}
                    >
                      Amount:
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: "#2e7d32",
                      }}
                    >
                      ₹{ContraToApprove?.cash_amount || "0.00"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      sx={{ fontSize: "0.65rem", color: "#666", mb: 0.3 }}
                    >
                      Type:
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
                      {ContraToApprove?.transfer_type || "Bank to Bank"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      sx={{ fontSize: "0.65rem", color: "#666", mb: 0.3 }}
                    >
                      Created By:
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
                      {ContraToApprove?.created_by_name || "-"}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Details Card */}
              <Box
                sx={{
                  bgcolor: "#f5f5f5",
                  p: 1.5,
                  borderRadius: 1,
                  mb: 1.5,
                }}
              >
                {/* BANK TO BANK */}
                {ContraToApprove?.voucher_type === "BANK TO BANK" && (
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        mb: 1,
                        color: "#1976d2",
                        textAlign: "center",
                        pb: 0.5,
                        borderBottom: "1px solid #ddd",
                      }}
                    >
                      BANK TO BANK
                    </Typography>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Box>
                        <Typography sx={{ fontSize: "0.65rem", color: "#666" }}>
                          From Bank:
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            wordBreak: "break-word",
                          }}
                        >
                          {ContraToApprove?.from_account_name || "-"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "0.65rem", color: "#666" }}>
                          To Bank:
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            wordBreak: "break-word",
                          }}
                        >
                          {ContraToApprove?.to_account_name || "-"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* CASH TO BANK */}
                {ContraToApprove?.voucher_type === "CASH TO BANK" && (
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        mb: 1,
                        color: "#1976d2",
                        textAlign: "center",
                        pb: 0.5,
                        borderBottom: "1px solid #ddd",
                      }}
                    >
                      CASH TO BANK
                    </Typography>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Box>
                        <Typography sx={{ fontSize: "0.65rem", color: "#666" }}>
                          From:
                        </Typography>
                        <Typography
                          sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                        >
                          Cash Account
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "0.65rem", color: "#666" }}>
                          To Bank:
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            wordBreak: "break-word",
                          }}
                        >
                          {ContraToApprove?.to_account_name || "-"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "0.65rem", color: "#666" }}>
                          Deposit Date:
                        </Typography>
                        <Typography
                          sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                        >
                          {ContraToApprove?.date || "-"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "0.65rem", color: "#666" }}>
                          Deposited By:
                        </Typography>
                        <Typography
                          sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                        >
                          {ContraToApprove?.created_by_name || "-"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "0.65rem", color: "#666" }}>
                          Amount in Words:
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            fontWeight: 500,
                            wordBreak: "break-word",
                            fontStyle: "italic",
                          }}
                        >
                          {toWords.convert(ContraToApprove?.cash_amount || 0)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* BANK TO CASH */}
                {ContraToApprove?.voucher_type === "BANK TO CASH" && (
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        mb: 1,
                        color: "#1976d2",
                        textAlign: "center",
                        pb: 0.5,
                        borderBottom: "1px solid #ddd",
                      }}
                    >
                      BANK TO CASH
                    </Typography>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      <Box>
                        <Typography sx={{ fontSize: "0.65rem", color: "#666" }}>
                          From Bank:
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 500,
                            wordBreak: "break-word",
                          }}
                        >
                          {ContraToApprove?.from_account_name || "-"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "0.65rem", color: "#666" }}>
                          To Account:
                        </Typography>
                        <Typography
                          sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                        >
                          Cash Account
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "0.65rem", color: "#666" }}>
                          Cheque/Ref No:
                        </Typography>
                        <Typography
                          sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                        >
                          {ContraToApprove?.cheque_no || "-"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "0.65rem", color: "#666" }}>
                          Withdrawn By:
                        </Typography>
                        <Typography
                          sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                        >
                          {ContraToApprove?.created_by_name || "-"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "0.65rem", color: "#666" }}>
                          Amount in Words:
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.7rem",
                            fontWeight: 500,
                            wordBreak: "break-word",
                            fontStyle: "italic",
                          }}
                        >
                          {toWords.convert(ContraToApprove?.cash_amount || 0)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Narration */}
                {ContraToApprove?.narration && (
                  <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid #ddd" }}>
                    <Typography
                      sx={{ fontSize: "0.65rem", color: "#666", mb: 0.3 }}
                    >
                      Narration:
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        wordBreak: "break-word",
                      }}
                    >
                      {ContraToApprove?.narration}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            // 🖥️ DESKTOP TABLE VIEW
            <>
              {/* Header Table */}
              <Box sx={{ overflowX: "auto" }}>
                <Table
                  size="small"
                  sx={{
                    width: "100%",
                    minWidth: 500,
                    borderCollapse: "collapse",
                    fontSize: "14px",
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    overflow: "hidden",
                    "& .MuiTableCell-root": {
                      padding: "10px 16px",
                      fontSize: "14px",
                      border: "1px solid #e0e0e0",
                    },
                  }}
                >
                  <TableBody>
                    <TableRow sx={{ bgcolor: "#d9d8d8ff" }}>
                      <TableCell
                        colSpan={4}
                        align="center"
                        sx={{ fontWeight: "bold" }}
                      >
                        CONTRA VOUCHER
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ width: "15%" }}>
                        <b>Voucher No.</b>
                      </TableCell>
                      <TableCell sx={{ width: "35%" }}>
                        : {ContraToApprove?.voucher_no || "-"}
                      </TableCell>
                      <TableCell sx={{ width: "15%" }}>
                        <b>Status</b>
                      </TableCell>
                      <TableCell sx={{ width: "35%" }}>
                        : {ContraToApprove?.status || "Pending"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <b>Date</b>
                      </TableCell>
                      <TableCell>
                        :{" "}
                        {ContraToApprove?.date
                          ? dayjs(ContraToApprove.date).format("DD/MM/YYYY")
                          : "-"}
                      </TableCell>
                      <TableCell>
                        <b>Amount</b>
                      </TableCell>
                      <TableCell>
                        : ₹{ContraToApprove?.cash_amount || "0.00"}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <b>Transfer Type</b>
                      </TableCell>
                      <TableCell>
                        : {ContraToApprove?.transfer_type || "Bank to Bank"}
                      </TableCell>
                      <TableCell>
                        <b>Created By</b>
                      </TableCell>
                      <TableCell>
                        : {ContraToApprove?.created_by_name || "-"}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>

              {/* Details Table */}
              <Box sx={{ overflowX: "auto", mt: 1 }}>
                <Table
                  size="small"
                  sx={{
                    width: "100%",
                    minWidth: 500,
                    borderCollapse: "collapse",
                    fontSize: "14px",
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    overflow: "hidden",
                    "& .MuiTableCell-root": {
                      padding: "10px 16px",
                      fontSize: "14px",
                      border: "1px solid #e0e0e0",
                    },
                  }}
                >
                  <TableBody>
                    {/* BANK TO BANK */}
                    {ContraToApprove?.voucher_type === "BANK TO BANK" && (
                      <>
                        <TableRow sx={{ bgcolor: "#d9d8d8ff" }}>
                          <TableCell
                            colSpan={4}
                            align="center"
                            sx={{ fontWeight: "bold" }}
                          >
                            BANK TO BANK
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <b>From Bank</b>
                          </TableCell>
                          <TableCell>
                            : {ContraToApprove?.from_account_name || "-"}
                          </TableCell>
                          <TableCell>
                            <b>To Bank</b>
                          </TableCell>
                          <TableCell>
                            : {ContraToApprove?.to_account_name || "-"}
                          </TableCell>
                        </TableRow>
                      </>
                    )}

                    {/* CASH TO BANK */}
                    {ContraToApprove?.voucher_type === "CASH TO BANK" && (
                      <>
                        <TableRow sx={{ bgcolor: "#d9d8d8ff" }}>
                          <TableCell
                            colSpan={4}
                            align="center"
                            sx={{ fontWeight: "bold" }}
                          >
                            CASH TO BANK
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <b>From Cash</b>
                          </TableCell>
                          <TableCell>: Cash Account</TableCell>
                          <TableCell>
                            <b>To Bank</b>
                          </TableCell>
                          <TableCell>
                            : {ContraToApprove?.to_account_name || "-"}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <b>Cash Amount</b>
                          </TableCell>
                          <TableCell>
                            : {ContraToApprove?.cash_amount || "-"}
                          </TableCell>
                          <TableCell>
                            <b>Deposit Date</b>
                          </TableCell>
                          <TableCell>
                            : {ContraToApprove?.date || "-"}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <b>Deposited By</b>
                          </TableCell>
                          <TableCell>
                            : {ContraToApprove?.created_by_name || "-"}
                          </TableCell>
                          <TableCell>
                            <b>Amount in Words</b>
                          </TableCell>
                          <TableCell sx={{ wordBreak: "break-word" }}>
                            :{" "}
                            {toWords.convert(ContraToApprove?.cash_amount || 0)}
                          </TableCell>
                        </TableRow>
                      </>
                    )}

                    {/* BANK TO CASH */}
                    {ContraToApprove?.voucher_type === "BANK TO CASH" && (
                      <>
                        <TableRow sx={{ bgcolor: "#d9d8d8ff" }}>
                          <TableCell
                            colSpan={4}
                            align="center"
                            sx={{ fontWeight: "bold" }}
                          >
                            BANK TO CASH
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <b>From Bank</b>
                          </TableCell>
                          <TableCell>
                            : {ContraToApprove?.from_account_name || "-"}
                          </TableCell>
                          <TableCell>
                            <b>To Account</b>
                          </TableCell>
                          <TableCell>: Cash Account</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <b>Cash Amount</b>
                          </TableCell>
                          <TableCell>
                            : {ContraToApprove?.cash_amount || "-"}
                          </TableCell>
                          <TableCell>
                            <b>Cheque/Ref No.</b>
                          </TableCell>
                          <TableCell>
                            : {ContraToApprove?.cheque_no || "-"}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <b>Withdrawn By</b>
                          </TableCell>
                          <TableCell>
                            : {ContraToApprove?.created_by_name || "-"}
                          </TableCell>
                          <TableCell>
                            <b>Amount in Words</b>
                          </TableCell>
                          <TableCell sx={{ wordBreak: "break-word" }}>
                            :{" "}
                            {toWords.convert(ContraToApprove?.cash_amount || 0)}
                          </TableCell>
                        </TableRow>
                      </>
                    )}

                    {/* COMMON ROWS */}
                    <TableRow>
                      <TableCell>
                        <b>Narration</b>
                      </TableCell>
                      <TableCell colSpan={3} sx={{ wordBreak: "break-word" }}>
                        : {ContraToApprove?.narration || "-"}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </>
          )}

          {/* ✅ UPDATED: Remark Section */}
          {showRemarkForIndentId === ContraToApprove?.cv_id ? (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
              >
                Please enter remark before{" "}
                {actionType === "Approved" ? "approving" : "rejecting"}:
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={remarkInput}
                onChange={(e) => setRemarkInput(e.target.value)}
                placeholder="Enter remark"
                multiline
                rows={2}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  },
                }}
              />

              {/* ✅ Yes/No buttons - Two rows on mobile */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", sm: "auto auto" },
                  gap: 1,
                  mt: 2,
                  justifyContent: { xs: "stretch", sm: "flex-end" },
                }}
              >
                <Button
                  variant="contained"
                  color={actionType === "Approved" ? "success" : "error"}
                  size="small"
                  sx={{
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    py: { xs: 1, sm: 0.75 },
                  }}
                  onClick={() => {
                    if (!remarkInput.trim()) {
                      toast.error("Remark is required!");
                      return;
                    }
                    handleApprove({
                      ...ContraToApprove,
                      status: actionType,
                      remarks: remarkInput,
                    });
                    setApproveAnchorEl(null);
                    setContraToApprove(null);
                    setRemarkInput("");
                    setShowRemarkForIndentId(null);
                    setActionType(null);
                  }}
                >
                  Yes
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    py: { xs: 1, sm: 0.75 },
                  }}
                  onClick={() => {
                    setApproveAnchorEl(null);
                    setContraToApprove(null);
                    setRemarkInput("");
                    setShowRemarkForIndentId(null);
                    setActionType(null);
                  }}
                >
                  No
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="body2"
                sx={{ mb: 1.5, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
              >
                Do you want to approve or reject this Contra Voucher?
              </Typography>

              {/* ✅ UPDATED: Buttons in 2x2 Grid on Mobile */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr 1fr",
                    sm: "auto auto auto auto",
                  },
                  gap: 1,
                  justifyContent: { xs: "stretch", sm: "flex-end" },
                }}
              >
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  sx={{
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    py: { xs: 1, sm: 0.75 },
                  }}
                  onClick={() => {
                    setShowRemarkForIndentId(ContraToApprove?.cv_id);
                    setActionType("Approved");
                  }}
                  disabled={ContraToApprove?.status === "Approved"}
                >
                  Approve
                </Button>

                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  sx={{
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    py: { xs: 1, sm: 0.75 },
                  }}
                  onClick={() => {
                    setShowRemarkForIndentId(ContraToApprove?.cv_id);
                    setActionType("Rejected");
                  }}
                  disabled={ContraToApprove?.status === "Approved"}
                >
                  Reject
                </Button>

                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  sx={{
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    py: { xs: 1, sm: 0.75 },
                  }}
                  onClick={() => handleEditClick(ContraToApprove)}
                  disabled={ContraToApprove?.status === "Approved"}
                >
                  Edit
                </Button>

                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  sx={{
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    py: { xs: 1, sm: 0.75 },
                  }}
                  onClick={() => {
                    setApproveAnchorEl(null);
                    setContraToApprove(null);
                  }}
                >
                  Close
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Popover>

      <Dialog open={editOpen} onClose={handleCloseEdit} fullWidth maxWidth="sm">
        <DialogTitle>Edit Contra Voucher</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Voucher No"
                size="small"
                fullWidth
                value={editForm.voucher_no}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                type="date"
                label="Date"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={editForm.date || ""}
                onChange={(e) => handleEditChange("date", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Voucher Type"
                size="small"
                fullWidth
                value={editForm.voucher_type || ""}
                onChange={(e) =>
                  handleEditChange("voucher_type", e.target.value)
                }
              >
                {typeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Amount"
                type="number"
                size="small"
                fullWidth
                value={editForm.cash_amount}
                onChange={(e) =>
                  handleEditChange("cash_amount", e.target.value)
                }
                onWheel={(e) => e.target.blur()}
                inputProps={{ min: 0, step: "0.01" }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Narration"
                size="small"
                fullWidth
                multiline
                rows={2}
                value={editForm.narration || ""}
                onChange={(e) => handleEditChange("narration", e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancel</Button>
          <Button
            onClick={handleUpdate}
            variant="contained"
            disabled={actionLoading}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ContraVoucher;
